import { useMemo, type ReactElement } from 'react'
import { type AppId, type CompanyId, filterArchived, isDefined, isEmpty, isNotEmpty } from '@terros/common'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useDisclosure } from '@mantine/hooks'
import { Badge, Stack } from '@mantine/core'
import { useQueryDisclosure } from '@hooks'
import { useConnectApp, useConnectInstallCount, useConnectInstalls, useProfile, useTranslation } from '@front/model'
import { ButtonAdd, NotReady, Page, TAuditInfoSection, TListItem } from '@components'
import { CONNECT_MARKETPLACE_HREF, parseConnectAppId } from '../integration/util'
import { AppDescription, ConnectAppLogo } from '../integration/ui'
import { ConnectAccessGuard } from '../integration/access'
import { AddInstallation } from '../integration.install/AddInstallation'
import { VersionsDrawer } from './VersionsDrawer'

function filterByCompany<T extends { companyId: CompanyId }>(
  items: T[] | undefined,
  companyId: CompanyId | undefined
): T[] {
  return (items ?? []).filter((item) => !companyId || item.companyId === companyId)
}

function filterByApp<T extends { appId: AppId }>(items: T[] | undefined, appId: AppId): T[] {
  return (items ?? []).filter((item) => item.appId === appId)
}

function ConnectMarketplaceAppPage(): ReactElement {
  const { appId } = Route.useParams()
  const parsedAppId = parseConnectAppId(appId)

  if (!parsedAppId) {
    return (
      <ConnectAccessGuard>
        <NotReady error='Invalid app id' id='marketplace app' />
      </ConnectAccessGuard>
    )
  }

  return <MarketplaceAppWorkspace appId={parsedAppId} />
}

function MarketplaceAppWorkspace({ appId }: { appId: AppId }): ReactElement {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [isAddOpen, { close: closeAdd, open: openAdd }] = useQueryDisclosure('add')
  const [versionsOpened, { close: closeVersions, open: openVersions }] = useDisclosure(false)
  const { company } = useProfile()
  const appState = useConnectApp(appId)
  const installsState = useConnectInstalls()
  const installCountState = useConnectInstallCount({ appId })

  const installs = Array.isArray(installsState.data) ? installsState.data : undefined
  const loadedApp = appState.data?.app
  const companyInstalls = useMemo(
    () =>
      isDefined(loadedApp)
        ? filterByCompany(filterByApp(filterArchived(installs), loadedApp.appId), company?.companyId)
        : [],
    [company?.companyId, installs, loadedApp]
  )
  const totalInstallCount = installCountState.data
  const isLoading = appState.isLoading || installsState.isLoading || installCountState.isLoading
  const error = appState.error || installsState.error || installCountState.error
  const isFetching = appState.isFetching || installsState.isFetching || installCountState.isFetching
  const refetch = (): void => {
    Promise.all([appState.refetch(), installsState.refetch(), installCountState.refetch()])
  }

  if (!isDefined(appState.data) || !isDefined(installs) || !isDefined(totalInstallCount)) {
    return (
      <ConnectAccessGuard>
        <NotReady
          data={appState.data ?? installs ?? totalInstallCount}
          error={error}
          id={t('marketplace app')}
          isLoading={isLoading}
        />
      </ConnectAccessGuard>
    )
  }

  const appData = appState.data
  const app = appData.app
  const [existingInstall] = companyInstalls
  const canInstall = isNotEmpty(app.currentVersion) && isEmpty(companyInstalls)

  return (
    <ConnectAccessGuard>
      <Page>
        <Page.Heading
          back
          breadcrumbs={[
            { href: CONNECT_MARKETPLACE_HREF, label: t('Marketplace') },
            { disabled: true, href: '#', label: app.name },
          ]}
          handleGoBack={() => navigate({ to: CONNECT_MARKETPLACE_HREF })}
          isFetching={isFetching}
          refetch={refetch}
          leftSection={
            <ConnectAppLogo avatarBlurhash={app.avatarBlurhash} avatarUrl={app.avatarUrl} name={app.name} size='sm' />
          }
          title={app.name}
        >
          {isNotEmpty(app.currentVersion) && (
            <Badge
              aria-label={t('View past versions')}
              color='blue'
              component='button'
              radius='xl'
              style={{ cursor: 'pointer' }}
              tt='none'
              type='button'
              variant='light'
              onClick={openVersions}
            >
              v{app.currentVersion}
            </Badge>
          )}
          {canInstall && <ButtonAdd onClick={openAdd}>{t('Install')}</ButtonAdd>}
        </Page.Heading>
        <Page.Content scrollArea>
          <Stack gap='xl'>
            <Stack>
              <Badge color='teal' radius='xl' variant='light'>
                {t('{{count}} total installs', { count: totalInstallCount })}
              </Badge>
              <AppDescription description={app.description} />
            </Stack>

            <TAuditInfoSection {...app}>
              <TListItem canCopy detailBold details={app.appId} label={t('App ID')} />
              <TListItem canCopy detailBold details={app.companyId} label={t('Company ID')} />
              {existingInstall && (
                <TListItem canCopy detailBold details={existingInstall.installId} label={t('Install ID')} />
              )}
            </TAuditInfoSection>
          </Stack>
        </Page.Content>
      </Page>

      {canInstall && <AddInstallation appData={appData} opened={isAddOpen} onClose={closeAdd} />}
      <VersionsDrawer
        currentVersion={app.currentVersion}
        installedVersion={existingInstall?.appVersion}
        opened={versionsOpened}
        versions={appData.versions}
        onClose={closeVersions}
      />
    </ConnectAccessGuard>
  )
}

export const Route = createFileRoute('/integration/marketplace_/$appId/')({
  component: ConnectMarketplaceAppPage,
})
